import { FC, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { UserX } from 'lucide-react';
import { usePermissions } from '../../modules/auth/hooks/usePermissions';
import { ConfirmDialog, TOKENS } from '../../components/design-system';

const API_URL = process.env.REACT_APP_API_URL;

interface Props {
  userId: number | string | null | undefined;
  userName: string;
  currentUserId: number | undefined;
  /** Chamado após cancelar com sucesso — fecha modal + refetch. */
  onCancelled: () => void;
}

/**
 * Botão "Cancelar usuário" — rodapé dos modais de edição de
 * operator/coord/manager/admin. Só aparece pra caller com
 * is_admin_anywhere e nunca em cima do próprio user (guarda
 * duplicada com a validação do backend).
 *
 * Efeito: DELETE /users/:id/cancel — apaga todos os role_users e
 * seta status=17. Não é reversível pela UI.
 */
export const CancelUserButton: FC<Props> = ({ userId, userName, currentUserId, onCancelled }) => {
  const perms = usePermissions();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Guarda: precisa ser admin em algum lugar
  if (!perms.is_admin_anywhere) return null;
  // Só faz sentido em modo edição (user existente)
  if (!userId) return null;
  // Não pode cancelar a si mesmo
  if (currentUserId && +userId === +currentUserId) return null;

  const doCancel = async () => {
    setCancelling(true);
    try {
      const { data } = await axios.delete<{ data: { removed_role_links: number } }>(
        `${API_URL}/users/${userId}/cancel`,
      );
      const n = data?.data?.removed_role_links ?? 0;
      toast.success(
        n > 0
          ? `${userName} cancelado. ${n} vínculo(s) removido(s).`
          : `${userName} cancelado.`
      );
      setConfirmOpen(false);
      onCancelled();
    } catch (e: any) {
      toast.error(`Erro ao cancelar: ${e?.response?.data?.message ?? e?.message ?? 'desconhecido'}`);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <button
        type='button'
        onClick={() => setConfirmOpen(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'transparent',
          color: TOKENS.color.severity.high.text,
          border: `1px solid ${TOKENS.color.severity.high.text}`,
          padding: '6px 12px', fontSize: 12, fontWeight: 700,
          borderRadius: TOKENS.radius.chip, cursor: 'pointer',
        }}
      >
        <UserX size={14} /> Cancelar usuário
      </button>
      <ConfirmDialog
        open={confirmOpen}
        title={`Cancelar ${userName}?`}
        description={
          <>
            O usuário <strong>{userName}</strong> será <strong>cancelado</strong> no sistema.
            <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
              <li>Todos os vínculos com empresas serão <strong>removidos</strong>.</li>
              <li>Ele <strong>não conseguirá mais fazer login</strong>.</li>
              <li>O histórico (logs de operação, notificações) fica preservado.</li>
              <li>Pra reativar, é necessário cadastrar de novo com o mesmo email.</li>
            </ul>
          </>
        }
        confirmLabel='Cancelar usuário'
        tone='danger'
        loading={cancelling}
        onCancel={() => { if (!cancelling) setConfirmOpen(false); }}
        onConfirm={doCancel}
      />
    </>
  );
};
